import { useState } from "react";
import Navbar from "../../components/layout/navbar";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import {
  Check,
  ShoppingCart,
  CreditCard,
  MessageSquare,
  AlertTriangle,
} from "lucide-react";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Pagination } from "../../components/ui/pagination";

interface ProfileData {
  username: string;
  email: string;
  phone: string;
  address: string;
  lastLogin: string;
}

type Notification = {
  id: string;
  title: string;
  message: string;
  time: string;
  read: boolean;
  type: "order" | "payment" | "message" | "alert";
  createdAt: Date; // 👈 required for grouping by day
};

const getNotificationIcon = (type: Notification["type"]) => {
  switch (type) {
    case "order":
      return <ShoppingCart className="h-4 w-4 text-green-700" />;
    case "payment":
      return <ShoppingCart className="h-4 w-4 text-green-700" />;
    case "message":
      return <ShoppingCart className="h-4 w-4 text-green-700" />;
    case "alert":
      return <ShoppingCart className="h-4 w-4 text-green-700" />;
    default:
      return null;
  }
};

const Notifications = () => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [tab, setTab] = useState<"all" | "unread">("all");

  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: "n1",
      title: "New Order",
      message: "Order #ORD-7829 has been placed by Chioma Eze",
      time: "2 mins ago",
      read: false,
      type: "order",
      createdAt: new Date(), // today
    },
    {
      id: "n2",
      title: "Payment Successful",
      message: "Payment of ₦12,500 received for order #ORD-7829",
      time: "3 mins ago",
      read: false,
      type: "payment",
      createdAt: new Date(), // today
    },
    {
      id: "n3",
      title: "Inventory Alert",
      message: "Tomatoes stock is critically low (35 units)",
      time: "15 mins ago",
      read: false,
      type: "alert",
      createdAt: new Date(new Date().setDate(new Date().getDate() - 1)), // yesterday
    },
    {
      id: "n4",
      title: "New Message",
      message: "You have a new message from Fresh Foods Ltd",
      time: "1 hour ago",
      read: true,
      type: "message",
      createdAt: new Date(new Date().setDate(new Date().getDate() - 1)), // yesterday
    },
    {
      id: "n5",
      title: "Order Updated",
      message: 'Order #ORD-7825 status changed to "Delivered"',
      time: "2 days ago",
      read: true,
      type: "order",
      createdAt: new Date(new Date().setDate(new Date().getDate() - 2)), // older
    },
  ]);

  const groupByDay = (list: Notification[]) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const groups: Record<string, Notification[]> = {};

    list.forEach((notification) => {
      const date = new Date(notification.createdAt);
      let label = "Older";

      if (date.toDateString() === today.toDateString()) {
        label = "Today";
      } else if (date.toDateString() === yesterday.toDateString()) {
        label = "Yesterday";
      }

      if (!groups[label]) {
        groups[label] = [];
      }

      groups[label].push(notification);
    });

    return groups;
  };

  const groupedNotifications = groupByDay(
    tab === "all" ? notifications : notifications.filter((n) => !n.read)
  );

  const unreadCount = notifications.filter((n) => !n.read).length;

  const filteredNotifications =
    tab === "all" ? notifications : notifications.filter((n) => !n.read);

  const markAllAsRead = () => {
    const updated = notifications.map((n) => ({ ...n, read: true }));
    setNotifications(updated);
  };

  const markOneAsRead = (id: string) => {
    const updated = notifications.map((n) =>
      n.id === id ? { ...n, read: true } : n
    );
    setNotifications(updated);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar profile={profile as ProfileData} />

      <main className="flex-1 container mx-auto py-6">
        {/* Header */}
        <div className="flex sm:flex-row sm:items-center justify-between mb-6">
          <h1 className="text-[30px] font-extrabold text-black">
            Notifications
          </h1>
          <Button
            size="sm"
            variant="default"
            type="button"
            className="font-normal md:mt-0 mt-6 bg-green-700 text-white rounded-full"
          >
            Send Notification
          </Button>
        </div>

        <Card>
          <div className="flex justify-between items-center px-6 pt-6">
            <h2 className="text-[20px] font-bold text-black">
              All Notifications
            </h2>
            <div className="flex gap-2">
              <button
                className="flex items-center gap-1 text-sm text-gray-700 hover:text-black"
                onClick={markAllAsRead}
              >
                <Check className="h-3 w-3" />
                Mark all as read
              </button>
              <Button variant="outline" className="rounded-full">
                All
              </Button>
              <Button variant="outline" className="rounded-full">
                Type
              </Button>
            </div>
          </div>

          <ScrollArea className="h-[320px] px-6 pb-6 mt-4">
            {Object.entries(groupedNotifications).map(([dayLabel, notifs]) => (
              <div key={dayLabel} className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 mb-2">
                  {dayLabel}
                </h3>
                <div className="divide-y">
                  {notifs.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => markOneAsRead(notification.id)}
                      className={`px-4 py-3 cursor-pointer transition-colors duration-200 ${
                        !notification.read
                          ? "bg-gray-50 hover:bg-gray-100"
                          : "bg-white hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {!notification.read && (
                          <span className="h-2 w-2 bg-green-500 rounded-full mt-2"></span>
                        )}
                        <div className="flex-shrink-0 p-2 rounded-full bg-gray-100">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-sm font-semibold text-gray-800">
                              {notification.title}
                            </p>
                            <span className="text-[11px] text-gray-400 whitespace-nowrap">
                              {notification.time}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {notification.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </ScrollArea>
        </Card>
      </main>
    </div>
  );
};

export default Notifications;
